import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Bell, Calculator, Shield, Globe, ArrowRightLeft, Upload, Download, RefreshCw, Zap, Database, Loader2, Plus, Search, X, CheckCircle, Calendar, Clock, Filter, ChevronDown, ChevronUp, FileText, RefreshCcw, Book, XCircle, Receipt, Users, Building2 } from 'lucide-react';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, dispatchFiscalYearRefresh } from './utils/comptabiliteListPageUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useSettings } from '../../core/contexts/SettingsContext';
import { getCurrencies, updateExchangeRates } from '../../shared/utils/comptabiliteService';
import { logger } from '../../shared/utils/logger';
import ReactDOM from 'react-dom';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, isBrandCurrencyLocked, SAUDI_CURRENCY_CODE, filterBrandCurrencyRows } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';


/**
 * Instant Global Search (Spotlight-style)
 * Search across accounts, entries, auxiliaries, assets
 */
const GlobalSearchDialog = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.comptabiliteConfigPage.${key}`, opts);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({ accounts: [], entries: [], auxiliaries: [], assets: [], total_results: 0 });
  const [isSearching, setIsSearching] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Focus search input when opened
      setTimeout(() => searchInputRef.current?.focus(), 100);
      
      // Add keyboard listener for Esc
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    // Instant search as you type
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        performSearch();
      } else {
        setResults({ accounts: [], entries: [], auxiliaries: [], assets: [], total_results: 0 });
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const performSearch = async () => {
    setIsSearching(true);
    try {
      const response = await fetch('/api/comptabilite/config/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          search_term: searchTerm,
          exact_match: false,
          case_sensitive: false,
          categories: ['accounts', 'entries', 'auxiliaries', 'assets']
        })
      });

      const result = await response.json();
      if (result.success) {
        setResults(result.data);
      }
    } catch (error) {
      logger.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const categoryIcons = {
    account: FileText,
    entry: Receipt,
    auxiliary: Users,
    asset: Building2
  };

  const categoryColors = {
    account: 'text-blue-400',
    entry: 'text-green-400',
    auxiliary: 'text-purple-400',
    asset: 'text-orange-400'
  };

  const categoryLabels = {
    accounts: p('globalSearch_category_accounts'),
    entries: p('globalSearch_category_entries'),
    auxiliaries: p('globalSearch_category_auxiliaries'),
    assets: p('globalSearch_category_assets')
  };

  const allResults = [
    ...results.accounts.map(r => ({ ...r, category: 'account' })),
    ...results.entries.map(r => ({ ...r, category: 'entry' })),
    ...results.auxiliaries.map(r => ({ ...r, category: 'auxiliary' })),
    ...results.assets.map(r => ({ ...r, category: 'asset' }))
  ];

  const filteredResults = activeCategory === 'all' 
    ? allResults 
    : allResults.filter(r => r.category === activeCategory);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-start justify-center z-[99999] pt-20 px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border-2 border-blue-400/50 rounded-2xl overflow-hidden max-w-3xl w-full max-h-[80vh] flex flex-col"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4)' }}
      >
        {/* Search Input */}
        

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searchTerm.length < 2 ? (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto mb-4 text-white/20" />
              <p className="text-white/50">{p('globalSearch_min_chars')}</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto mb-4 text-white/20" />
              <p className="text-white/50">{p('globalSearch_no_results')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredResults.map((result, index) => {
                const Icon = categoryIcons[result.category];
                const colorClass = categoryColors[result.category];

                return (
                  <motion.div
                    key={`${result.category}-${result.id || index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="bg-white/5 border border-white/10 rounded-xl p-3 hover:border-blue-400/40 hover:bg-white/10 transition-all cursor-pointer group"
                    onClick={() => {
                      // Navigate to detail view
                      onClose();
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg bg-white/10 group-hover:scale-110 transition-transform`}>
                        <Icon size={18} className={colorClass} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-semibold text-sm">{result.code || result.piece}</span>
                          <span className="text-xs text-white/50 uppercase">{result.category}</span>
                        </div>
                        <p className="text-white/80 text-sm truncate">{result.libelle || result.label}</p>
                        {result.balance !== undefined && (
                          <p className="text-blue-300 text-xs mt-1">{formatBrandMoney(result.balance)}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 bg-white/5">
          <p className="text-white/50 text-xs text-center">
            {p('globalSearch_footer')}
          </p>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};





/**
 * Modern Period Selector Component
 * Advanced period selection with better UX
 */
const PeriodSelector = ({ value, onChange, showRange = false, className = '' }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.comptabiliteConfigPage.${key}`, opts);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const periods = [
    { id: '01', label: p('period_jan'), type: 'month' },
    { id: '02', label: p('period_feb'), type: 'month' },
    { id: '03', label: p('period_mar'), type: 'month' },
    { id: '04', label: p('period_apr'), type: 'month' },
    { id: '05', label: p('period_may'), type: 'month' },
    { id: '06', label: p('period_jun'), type: 'month' },
    { id: '07', label: p('period_jul'), type: 'month' },
    { id: '08', label: p('period_aug'), type: 'month' },
    { id: '09', label: p('period_sep'), type: 'month' },
    { id: '10', label: p('period_oct'), type: 'month' },
    { id: '11', label: p('period_nov'), type: 'month' },
    { id: '12', label: p('period_dec'), type: 'month' },
    { id: 'EX', label: p('period_exercise'), type: 'special' },
    { id: 'RE', label: p('period_reopening'), type: 'special' },
    { id: 'ALL', label: p('period_all'), type: 'special' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (period) => {
    onChange(period.id);
    setIsOpen(false);
  };

  const selectedPeriod = periods.find(p => p.id === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm font-medium hover:bg-white/15 transition-all duration-300 flex items-center justify-between"
      >
        <div className="flex items-center space-x-2">
          <Calendar size={16} className="text-blue-400" />
          <span>{selectedPeriod?.label || p('period_select')}</span>
        </div>
        <ChevronDown size={16} className={`text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 mt-2 w-full bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border border-blue-400/50 rounded-xl shadow-2xl overflow-hidden"
            style={{ boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)' }}
          >
            <div className="max-h-[400px] overflow-y-auto">
              {/* Monthly periods */}
              <div className="p-2">
                <div className="text-xs font-semibold text-blue-300 px-3 py-2">{p('period_monthly_header')}</div>
                {periods.filter(p => p.type === 'month').map(period => (
                  <button
                    key={period.id}
                    onClick={() => handleSelect(period)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${value === period.id 
                        ? 'bg-blue-500/30 text-white' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>

              {/* Special periods */}
              <div className="p-2 border-t border-blue-400/20">
                <div className="text-xs font-semibold text-purple-300 px-3 py-2">{p('period_special_header')}</div>
                {periods.filter(p => p.type === 'special').map(period => (
                  <button
                    key={period.id}
                    onClick={() => handleSelect(period)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${value === period.id 
                        ? 'bg-purple-500/30 text-white' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Dual Period Selector (Du / Au)
 */
export const DualPeriodSelector = ({ valueFrom, valueTo, onChangeFrom, onChangeTo, className = '' }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.comptabiliteConfigPage.${key}`, opts);
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <div className="flex-1">
        <label className="block w-full text-white/70 text-xs mb-1.5 font-medium text-right">{p('period_from')}</label>
        <PeriodSelector value={valueFrom} onChange={onChangeFrom} />
      </div>
      <div className="flex-1">
        <label className="block w-full text-white/70 text-xs mb-1.5 font-medium text-right">{p('period_to')}</label>
        <PeriodSelector value={valueTo} onChange={onChangeTo} />
      </div>
    </div>
  );
};





/**
 * AutoBackupPrompt Component (Stub)
 * This component is imported but not currently used per user request
 * Created to prevent import errors
 */
const AutoBackupPrompt = ({ isOpen, onClose, operationType, onConfirm }) => {
  // Stub component - not rendered
  if (!isOpen) return null;
  
  return null;
};




const SAUDI_DEMO_CURRENCY_ROW = {
  currency_code: SAUDI_CURRENCY_CODE,
  currency_nameKey: 'currency_sar',
  rate: 1.0,
  is_base: 1,
  volume: 0,
};

const LEGACY_DEMO_CURRENCY_ROWS = [
  { currency_code: 'DZD', currency_nameKey: 'currency_dzd', rate: 1.0, is_base: 1, volume: 0 },
  { currency_code: 'EUR', currency_nameKey: 'currency_eur', rate: 146.85, is_base: 0, volume: 2850000 },
  { currency_code: 'USD', currency_nameKey: 'currency_usd', rate: 135.2, is_base: 0, volume: 1650000 },
  { currency_code: 'GBP', currency_nameKey: 'currency_gbp', rate: 165.75, is_base: 0, volume: 450000 },
];

function getCurrencyVolume(currency) {
  return Number(currency?.volume ?? currency?.volume_dzd) || 0;
}

function getDemoCurrencyRows() {
  return isSaudiBrand() ? [SAUDI_DEMO_CURRENCY_ROW] : LEGACY_DEMO_CURRENCY_ROWS;
}

const ComptabiliteConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.comptabiliteConfigPage.${key}`, opts);
  const { config: accountingConfig, patchConfig } = useComptabiliteConfig();
  const { saveCurrentSettings, resetSettings } = useSettings();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('accounting');
  const [activeUtilityTab, setActiveUtilityTab] = useState('currencies');
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [updatingRates, setUpdatingRates] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showAutoBackupPrompt, setShowAutoBackupPrompt] = useState(false);

  // Module config from useComptabiliteConfig hook (hydrated on mount)

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCurrentSettings();
      await localApiAdapter.put('/comptabilite/module-config', accountingConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      logger.error('Error saving accounting config:', error);
      alert(p('error_save') || p('save_success'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm(p('confirm_reset'))) {
      try {
        await resetSettings();
        window.location.reload();
      } catch (error) {
        logger.error('Error resetting accounting config:', error);
        alert(p('error_save') || p('reset_failed'));
      }
    }
  };

  const updateConfig = (key, value) => {
    patchConfig(key, value);
  };

  const updateEntityPrefix = (prefixKey, value) => {
    patchConfig('entityPrefixes', {
      ...(accountingConfig.entityPrefixes || {}),
      [prefixKey]: value,
    });
  };

  const entityPrefixFields = [
    { key: 'journalEntry', label: p('prefix_journal_entry', { defaultValue: 'بادئة القيود' }) },
    { key: 'balance', label: p('prefix_balance', { defaultValue: 'بادئة ميزان المراجعة' }) },
    { key: 'grandLivre', label: p('prefix_grand_livre', { defaultValue: 'بادئة الدفتر العام' }) },
    { key: 'bilan', label: p('prefix_bilan', { defaultValue: 'بادئة الميزانية' }) },
    { key: 'accountChart', label: p('prefix_account_chart', { defaultValue: 'بادئة دليل الحسابات' }) },
    { key: 'taxDeclaration', label: p('prefix_tax_declaration', { defaultValue: 'بادئة الإقرارات' }) },
    { key: 'analyticalCode', label: p('prefix_analytical', { defaultValue: 'بادئة الرموز التحليلية' }) },
  ];

  // Load real currencies from backend
  useEffect(() => {
    loadCurrencies();
  }, []);

  useEffect(() => {
    if (!isBrandCurrencyLocked()) return;
    if (accountingConfig.defaultCurrency !== SAUDI_CURRENCY_CODE) {
      updateConfig('defaultCurrency', SAUDI_CURRENCY_CODE);
    }
    if (accountingConfig.enableMultiCurrency) {
      updateConfig('enableMultiCurrency', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enforce SAR once on load for Qawaem
  }, []);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      const currenciesData = await getCurrencies();
      if (isBrandCurrencyLocked()) {
        setCurrencies(getDemoCurrencyRows());
      } else {
        setCurrencies(filterBrandCurrencyRows(currenciesData));
      }
    } catch (err) {
      logger.error('Error loading currencies:', err);
      setCurrencies(getDemoCurrencyRows());
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRates = async () => {
    try {
      setUpdatingRates(true);
      if (isBrandCurrencyLocked()) {
        await loadCurrencies();
      } else {
        const rates = currencies.map((c) => ({
          currency_code: c.currency_code,
          rate: c.rate,
        }));
        await updateExchangeRates(rates);
        await loadCurrencies();
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      logger.error('Error updating rates:', err);
      alert(p('error_update_rates', { message: err.message }));
    } finally {
      setUpdatingRates(false);
    }
  };

  const automationRules = [
    { nameKey: 'rule_auto_reconciliation', status: 'active', frequencyKey: 'opt_daily' },
    { nameKey: 'rule_vat_threshold', status: 'active', frequencyKey: 'opt_monthly' },
    { nameKey: 'rule_provisional_closing', status: 'inactive', frequencyKey: 'opt_monthly' },
  ];

  const renderUtilitiesSection = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Database className="h-6 w-6" />
          {p('section_utilities')}
        </h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleUpdateRates}
            disabled={updatingRates}
            className="bg-blue-500/20 text-blue-300 px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-blue-500/30 disabled:opacity-60"
          >
            {updatingRates ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {p('btn_update_rates')}
          </button>
          <button type="button" className="bg-green-500/20 text-green-300 px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-green-500/30">
            <Download size={16} />
            {p('btn_export_fec')}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
        <button
          type="button"
          onClick={() => setActiveUtilityTab('currencies')}
          className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${activeUtilityTab === 'currencies' ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}
        >
          <Globe size={16} />
          {p('tab_currencies')}
        </button>
        <button
          type="button"
          onClick={() => setActiveUtilityTab('import-export')}
          className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${activeUtilityTab === 'import-export' ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}
        >
          <ArrowRightLeft size={16} />
          {p('tab_import_export')}
        </button>
      </div>

      {activeUtilityTab === 'currencies' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{p('currencies_title')}</h3>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
            </div>
          ) : (
            <div className="grid gap-3">
              {currencies.map((currency) => (
                <div key={currency.currency_code} className="bg-white/5 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <span className="text-blue-400 font-bold text-lg">{currency.currency_code}</span>
                    <span className="text-white/70 ml-3">{currency.currency_nameKey ? p(currency.currency_nameKey) : currency.currency_name}</span>
                    {getCurrencyVolume(currency) > 0 && (
                      <span className="text-white/50 text-sm ml-3">
                        {p('currency_volume', { amount: getCurrencyVolume(currency).toLocaleString() })}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-white font-semibold">{Number(currency.rate || 0).toFixed(2)}</span>
                    {currency.is_base === 1 && <span className="text-white/50 text-sm ml-2">{p('currency_base')}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeUtilityTab === 'import-export' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{p('import_export_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/5 rounded-lg p-6 text-center">
              <Upload className="h-12 w-12 text-blue-400 mx-auto mb-3" />
              <h4 className="text-white font-semibold mb-2">{p('import_title')}</h4>
              <p className="text-white/60 text-sm mb-4">{p('import_desc')}</p>
              <button type="button" className="bg-blue-500/20 text-blue-300 px-4 py-2 rounded-lg text-sm hover:bg-blue-500/30">
                {p('import_choose_file')}
              </button>
            </div>
            <div className="bg-white/5 rounded-lg p-6 text-center">
              <Download className="h-12 w-12 text-green-400 mx-auto mb-3" />
              <h4 className="text-white font-semibold mb-2">{p('export_title')}</h4>
              <p className="text-white/60 text-sm mb-4">{p('export_desc')}</p>
              <button type="button" className="bg-green-500/20 text-green-300 px-4 py-2 rounded-lg text-sm hover:bg-green-500/30">
                {p('export_fec_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderLiasseSection = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 space-y-6">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Calculator className="h-6 w-6" />
        {p('liasse_title')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_inventory_method')}</label>
          <select className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="permanent">{p('opt_permanent')}</option>
            <option value="intermittent">{p('opt_intermittent')}</option>
          </select>
          <p className="text-white/40 text-xs mt-1">{p('inventory_hint')}</p>
        </div>

        <div>
          <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_export')}</label>
          <select className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="excel">{p('opt_excel')}</option>
            <option value="pdf">{p('opt_pdf')}</option>
          </select>
        </div>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 mb-6">
        <h4 className="text-white font-semibold mb-3">{p('print_options_title')}</h4>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500" />
            <span className="text-white/80 text-sm">{p('print_round_nearest')}</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500" />
            <span className="text-white/80 text-sm">{p('print_with_cents')}</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500" />
            <span className="text-white/80 text-sm">{p('print_page_numbers')}</span>
          </label>
        </div>
      </div>

      <div className="bg-white/5 rounded-lg p-4">
        <h4 className="text-white font-semibold mb-3">{p('table12_title')}</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm text-white/60 mb-2 font-medium text-right">{p('label_fees')}</label>
            <input
              type="text"
              defaultValue="622"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="622"
            />
          </div>
          <div>
            <label className="block w-full text-sm text-white/60 mb-2 font-medium text-right">{p('label_commissions')}</label>
            <input
              type="text"
              defaultValue="625"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="625"
            />
          </div>
          <div>
            <label className="block w-full text-sm text-white/60 mb-2 font-medium text-right">{p('label_subcontracting')}</label>
            <input
              type="text"
              defaultValue="611"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="611"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAutomationSection = () => (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 space-y-4">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Zap className="h-6 w-6" />
        {p('automation_title')}
      </h2>
      <div className="grid gap-3">
        {automationRules.map((rule, idx) => (
          <div key={idx} className="bg-white/5 rounded-lg p-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Zap className={`h-5 w-5 ${rule.status === 'active' ? 'text-green-400' : 'text-white/30'}`} />
              <div>
                <span className="text-white font-medium">{p(rule.nameKey)}</span>
                <span className="text-white/50 text-sm ml-3">{p(rule.frequencyKey)}</span>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs ${rule.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/50'}`}>
              {rule.status === 'active' ? p('status_active') : p('status_inactive')}
            </span>
          </div>
        ))}
        <button type="button" className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-4 text-white/70 border-2 border-dashed border-white/20 transition-all">
          + {p('add_rule')}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <ModuleConfigPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        saved={saved}
        savedMessage={p('save_success')}
        saving={saving}
        onSave={handleSave}
        onReset={handleReset}
        headerActions={(
          <button
            type="button"
            onClick={() => setShowGlobalSearch(true)}
            className="px-4 py-3 rounded-xl flex items-center gap-2 font-medium bg-white/5 text-white/70 hover:text-white hover:bg-white/10 transition-all"
          >
            <Search className="h-5 w-5" />
            {p('btn_global_search')}
          </button>
        )}
        tabs={[
          { id: 'accounting', label: p('section_accounting'), icon: Calculator, gradient: 'from-blue-500 to-blue-600' },
          { id: 'utilities', label: p('section_utilities'), icon: Database, gradient: 'from-teal-500 to-teal-600' },
          { id: 'liasse', label: p('tab_liasse'), icon: FileText, gradient: 'from-indigo-500 to-indigo-600' },
          { id: 'notifications', label: p('section_notifications'), icon: Bell, gradient: 'from-violet-500 to-violet-600' },
        ]}
        tabContent={{
          accounting: (
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Calculator className="h-6 w-6" />
                {p('section_accounting')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_fiscal_year_start')}</label>
                  <select value={accountingConfig.fiscalYearStart} onChange={(e) => updateConfig('fiscalYearStart', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="january">{p('opt_january')}</option>
                    <option value="april">{p('opt_april')}</option>
                    <option value="july">{p('opt_july')}</option>
                  </select>
                </div>
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_accounting_method')}</label>
                  <select value={accountingConfig.accountingMethod} onChange={(e) => updateConfig('accountingMethod', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="accrual">{p('opt_accrual')}</option>
                    <option value="cash">{p('opt_cash')}</option>
                  </select>
                </div>
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_tax_calculation')}</label>
                  <select value={accountingConfig.taxCalculation} onChange={(e) => updateConfig('taxCalculation', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="auto">{p('opt_auto')}</option>
                    <option value="manual">{p('opt_manual')}</option>
                  </select>
                </div>
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_chart_of_accounts')}</label>
                  <select value={accountingConfig.chartOfAccountsType} onChange={(e) => updateConfig('chartOfAccountsType', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="standard">{p('opt_standard')}</option>
                    <option value="custom">{p('opt_custom')}</option>
                  </select>
                </div>
                <div className="md:col-span-2 border-t border-white/10 pt-4 mt-2">
                  <h3 className="text-white font-semibold mb-3 text-right">
                    {p('section_entity_prefixes', { defaultValue: 'بادئات الرموز (رمز)' })}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {entityPrefixFields.map(({ key, label }) => (
                      <div key={key}>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
                        <input
                          type="text"
                          value={accountingConfig.entityPrefixes?.[key] || ''}
                          onChange={(e) => updateEntityPrefix(key, e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_reconciliation_frequency')}</label>
                  <select value={accountingConfig.reconciliationFrequency} onChange={(e) => updateConfig('reconciliationFrequency', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="daily">{p('opt_daily')}</option>
                    <option value="weekly">{p('opt_weekly')}</option>
                    <option value="monthly">{p('opt_monthly')}</option>
                  </select>
                </div>
                <div>
                  <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_currency')}</label>
                  <BrandCurrencySelect
                    value={accountingConfig.defaultCurrency}
                    onChange={(e) => updateConfig('defaultCurrency', e.target.value)}
                  />
                </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={accountingConfig.journalApprovalRequired} onChange={(e) => updateConfig('journalApprovalRequired', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                  <label className="ml-3 text-white/80">{p('label_journal_approval')}</label>
                </div>
                {!isBrandCurrencyLocked() ? (
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.enableMultiCurrency} onChange={(e) => updateConfig('enableMultiCurrency', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('label_multi_currency')}</label>
                  </div>
                ) : null}
              </div>
            </div>
          ),
          utilities: renderUtilitiesSection(),
          liasse: (
            <div className="space-y-6">
              {renderLiasseSection()}
              {renderAutomationSection()}
            </div>
          ),
          notifications: (
            <>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Bell className="h-6 w-6" />
                  {p('section_notifications')}
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.notifyJournalEntry} onChange={(e) => updateConfig('notifyJournalEntry', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('notify_journal')}</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.notifyReconciliation} onChange={(e) => updateConfig('notifyReconciliation', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('notify_reconciliation')}</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.emailNotifications} onChange={(e) => updateConfig('emailNotifications', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('notify_email')}</label>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  {p('section_security')}
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.restrictAccess} onChange={(e) => updateConfig('restrictAccess', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('restrict_access')}</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={accountingConfig.auditTrail} onChange={(e) => updateConfig('auditTrail', e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                    <label className="ml-3 text-white/80">{p('audit_trail')}</label>
                  </div>
                </div>
              </div>
            </>
          ),
        }}
      />
      <GlobalSearchDialog isOpen={showGlobalSearch} onClose={() => setShowGlobalSearch(false)} />
      <AutoBackupPrompt isOpen={showAutoBackupPrompt} onClose={() => setShowAutoBackupPrompt(false)} operationType="backup" onConfirm={() => setShowAutoBackupPrompt(false)} />
    </>
  );
};


const ConfigurationSituationPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.configurationSituationPage.${key}`, opts);
  // ===== STATE MANAGEMENT =====
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_configs');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Date range filter
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & Sorting
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('config_ref', (row) => row.config_ref ?? ''),
    textColumnRule('config_nom', (row) => row.config_nom ?? ''),
    textColumnRule('config_categorie', (row) => row.config_categorie ?? ''),
  ], []);
  
  // Expanded sections for GPS-style collapsible form (FIRST EXPANDED)
  const [comptabiliteConfigFormTab, setComptabiliteConfigFormTab] = useState('generalConfig');
  const comptabiliteConfigFormTabs = useMemo(() => ([
    { id: 'generalConfig', label: p('section_general'), icon: ChevronUp },
    { id: 'exerciceCompta', label: p('section_exercise'), icon: ChevronUp },
    { id: 'validationArchive', label: p('section_reports'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({
    config_ref: '',
    config_nom: '',
    config_description: '',
    config_categorie: '',
    config_cle: '',
    config_valeur: '',
    config_type: '',
    valeur_par_defaut: '',
    valeurs_autorisees: '',
    scope_config: '',
    scope_id: '',
    scope_nom: '',
    exercice_fiscal_courant: '',
    exercice_debut_date: '',
    exercice_fin_date: '',
    nombre_periodes_exercice: '',
    type_periodes: '',
    periodes_config: '',
    periodes_dates: '',
    plan_comptable_reference: '',
    plan_comptable_version: '',
    plan_comptable_personnalise: '',
    longueur_compte_min: '',
    longueur_compte_max: '',
    comptes_auxiliaires_actifs: '',
    comptes_analytiques_actifs: '',
    axes_analytiques_config: '',
    axes_nombre: '',
    devise_principale: '',
    devises_secondaires: '',
    taux_change_source: '',
    taux_change_mise_a_jour: '',
    seuils_materialite: '',
    seuils_alertes: '',
    regles_validation: '',
    workflow_validation: '',
    delais_cloture_config: '',
    echeances_reglementaires: '',
    rapports_obligatoires: '',
    rapports_periodicite: '',
    rapports_templates: '',
    rapports_destinataires_defaut: '',
    controles_automatiques: '',
    controles_actives: '',
    lettrage_automatique_actif: '',
    lettrage_regles: '',
    rapprochement_bancaire_actif: '',
    rapprochement_regles: '',
    integration_modules: '',
    interfaces_actives: '',
    synchronisation_auto: '',
    synchronisation_frequence: '',
    archivage_politique: '',
    archivage_duree_ans: '',
    backup_automatique: '',
    backup_frequence: '',
    backup_retention_jours: '',
    securite_acces: '',
    roles_permissions: '',
    audit_trail_actif: '',
    audit_retention_jours: '',
    notifications_config: '',
    notifications_destinataires: '',
    alertes_seuils: '',
    alertes_actives: '',
    langue_interface: '',
    format_date: '',
    format_nombre: '',
    format_montants: '',
    separateur_milliers: '',
    separateur_decimal: '',
    precision_calculs: '',
    arrondis_methode: '',
    impressions_templates: '',
    impressions_formats: '',
    export_formats_autorises: '',
    export_destinations: '',
    conformite_normes_actives: '',
    conformite_controles: '',
    options_avancees: '',
    features_experimentales: '',
    personnalisations_ui: '',
    dashboard_config: '',
    api_acces_actif: '',
    api_cles_config: '',
    api_rate_limits: '',
    maintenance_fenetre: '',
    maintenance_notifications: '',
    version_module: '',
    derniere_mise_a_jour: '',
    support_contact: '',
    documentation_url: '',
    actif_config: '',
    verrou_admin: '',
    derniere_modif_par: '',
    derniere_modif_date: '',
    approbation_requise: '',
    approuve_par: '',
    approbation_date: '',
    commentaires_config: '',
    notes_configuration: '',
    notes: '',
    metadata: ''
  });

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    loadConfigurations();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const filteredConfigurations = useMemo(() => {
    const base = configurations.filter(config => {
      if (dateRange.start || dateRange.end) {
        const configDate = new Date(config.created_at);

        if (dateRange.start) {
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          if (configDate < startOfDay) return false;
        }

        if (dateRange.end) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (configDate > endOfDay) return false;
        }
      }

      const matchesFilter = quickFilter === 'all' || config.config_categorie === quickFilter;

      const matchesSearch = !searchTerm ||
        config.config_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.config_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.config_cle?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [configurations, dateRange, quickFilter, searchTerm, columnFilters, columnFilterRules]);

  const filteredIds = useMemo(() => filteredConfigurations.map((c) => c.id), [filteredConfigurations]);
  const allRowsSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (
      prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]
    ));
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_delete_bulk', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/comptabilite/config/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'الإعدادات' }));
        setSelectedRecords([]);
        loadConfigurations();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/comptabilite/config');
      setConfigurations(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      
      if (selectedConfig) {
        await localApiAdapter.put(`/comptabilite/config/${selectedConfig.id}`, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الإعداد' }));
        if (payload.config_categorie === 'Exercice Fiscal') dispatchFiscalYearRefresh();
      } else {
        await localApiAdapter.post('/comptabilite/config', payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الإعداد' }));
        if (payload.config_categorie === 'Exercice Fiscal') dispatchFiscalYearRefresh();
      }
      await loadConfigurations();
      resetForm();
    } catch (error) {
      console.error('Error saving configuration:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setFormData({ ...config });
    setComptabiliteConfigFormTab('generalConfig');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/comptabilite/config/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الإعداد' }));
        await loadConfigurations();
      } catch (error) {
        console.error('Error deleting configuration:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (config) => {
    setSelectedConfig(config);
    setShowViewModal(true);
  };

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 1, labelKey: 'status_active', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 0, labelKey: 'status_inactive', icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ], p), [t]);

  const getConfigActiveStatus = (config) => (Number(config.actif_config) === 1 ? 1 : 0);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const config = configurations.find((c) => c.id === recordId);
      if (!config) return;
      await localApiAdapter.put(`/comptabilite/config/${recordId}`, { ...config, actif_config: newStatus });
      await loadConfigurations();
    } catch (error) {
      console.error('Error updating config status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      config_ref: '', config_nom: '', config_description: '', config_categorie: '', config_cle: '',
      config_valeur: '', config_type: '', valeur_par_defaut: '', valeurs_autorisees: '', scope_config: '',
      scope_id: '', scope_nom: '', exercice_fiscal_courant: '', exercice_debut_date: '', exercice_fin_date: '',
      nombre_periodes_exercice: '', type_periodes: '', periodes_config: '', periodes_dates: '',
      plan_comptable_reference: '', plan_comptable_version: '', plan_comptable_personnalise: '',
      longueur_compte_min: '', longueur_compte_max: '', comptes_auxiliaires_actifs: '', comptes_analytiques_actifs: '',
      axes_analytiques_config: '', axes_nombre: '', devise_principale: '', devises_secondaires: '',
      taux_change_source: '', taux_change_mise_a_jour: '', seuils_materialite: '', seuils_alertes: '',
      regles_validation: '', workflow_validation: '', delais_cloture_config: '', echeances_reglementaires: '',
      rapports_obligatoires: '', rapports_periodicite: '', rapports_templates: '', rapports_destinataires_defaut: '',
      controles_automatiques: '', controles_actives: '', lettrage_automatique_actif: '', lettrage_regles: '',
      rapprochement_bancaire_actif: '', rapprochement_regles: '', integration_modules: '', interfaces_actives: '',
      synchronisation_auto: '', synchronisation_frequence: '', archivage_politique: '', archivage_duree_ans: '',
      backup_automatique: '', backup_frequence: '', backup_retention_jours: '', securite_acces: '',
      roles_permissions: '', audit_trail_actif: '', audit_retention_jours: '', notifications_config: '',
      notifications_destinataires: '', alertes_seuils: '', alertes_actives: '', langue_interface: '',
      format_date: '', format_nombre: '', format_montants: '', separateur_milliers: '', separateur_decimal: '',
      precision_calculs: '', arrondis_methode: '', impressions_templates: '', impressions_formats: '',
      export_formats_autorises: '', export_destinations: '', conformite_normes_actives: '', conformite_controles: '',
      options_avancees: '', features_experimentales: '', personnalisations_ui: '', dashboard_config: '',
      api_acces_actif: '', api_cles_config: '', api_rate_limits: '', maintenance_fenetre: '',
      maintenance_notifications: '', version_module: '', derniere_mise_a_jour: '', support_contact: '',
      documentation_url: '', actif_config: '', verrou_admin: '', derniere_modif_par: '', derniere_modif_date: '',
      approbation_requise: '', approuve_par: '', approbation_date: '', commentaires_config: '',
      notes_configuration: '', notes: '', metadata: ''
    });
    setSelectedConfig(null);
    setShowModal(false);
    setComptabiliteConfigFormTab('generalConfig');
  };

  // Stats
  const stats = {
    total: configurations.length,
    actifs: configurations.filter(c => c.actif_config === 1).length,
    exerciceFiscal: configurations.filter(c => c.config_categorie === 'Exercice Fiscal').length,
    validations: configurations.filter(c => c.controles_actives === 1).length,
    derniereMAJ: configurations.length > 0 
      ? new Date(Math.max(...configurations.map(c => new Date(c.updated_at || c.created_at)))).toLocaleDateString('ar-SA')
      : '-'
  };

  // Main view tabs
  const mainTabs = [
    { id: 'all_configs', labelKey: 'tab_overview', icon: Settings },
    { id: 'by_category', labelKey: 'tab_by_category', icon: Book },
    { id: 'by_scope', labelKey: 'tab_by_scope', icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', labelKey: 'filter_all_configs', icon: Settings },
    { id: 'Général', labelKey: 'category_general', icon: Settings },
    { id: 'Exercice Fiscal', labelKey: 'category_fiscal_year', icon: Calendar },
    { id: 'Plan Comptable', labelKey: 'category_chart', icon: Book }
  ];

  const configDistribution = {
    all_configs: {
      titleKey: 'dist_overview_title',
      data: [
        { nameKey: 'dist_active', count: stats.actifs, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { nameKey: 'dist_inactive', count: stats.total - stats.actifs, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { nameKey: 'category_fiscal_year', count: stats.exerciceFiscal, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { nameKey: 'dist_with_validation', count: stats.validations, color: "bg-gradient-to-r from-emerald-500 to-emerald-600" }
      ]
    },
    by_category: {
      titleKey: 'dist_by_category_title',
      data: [
        { nameKey: 'category_general', count: configurations.filter(c => c.config_categorie === 'Général').length, color: "bg-gradient-to-r from-slate-500 to-slate-600" },
        { nameKey: 'category_fiscal_year', count: configurations.filter(c => c.config_categorie === 'Exercice Fiscal').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { nameKey: 'category_chart', count: configurations.filter(c => c.config_categorie === 'Plan Comptable').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { nameKey: 'category_reports', count: configurations.filter(c => c.config_categorie === 'Rapports').length, color: "bg-gradient-to-r from-violet-500 to-violet-600" }
      ]
    },
    by_scope: {
      titleKey: 'dist_by_scope_title',
      data: [
        { nameKey: 'scope_global', count: configurations.filter(c => c.scope_config === 'Global').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { nameKey: 'scope_exercise', count: configurations.filter(c => c.scope_config === 'Exercice').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { nameKey: 'scope_user', count: configurations.filter(c => c.scope_config === 'Utilisateur').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { nameKey: 'scope_department', count: configurations.filter(c => c.scope_config === 'Département').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    }
  };

  const distributionInfo = configDistribution[mainViewMode] || configDistribution.all_configs;

  const categoryLabel = (cat) => {
    const map = {
      'Général': 'category_general',
      'Exercice Fiscal': 'category_fiscal_year',
      'Plan Comptable': 'category_chart',
      'Clôture': 'category_closing',
      'Rapports': 'category_reports',
      'Validation': 'category_validation',
    };
    return map[cat] ? p(map[cat]) : cat;
  };

  const scopeLabel = (scope) => {
    const map = {
      Global: 'scope_global',
      Exercice: 'scope_exercise',
      Utilisateur: 'scope_user',
      Département: 'scope_department',
    };
    return map[scope] ? p(map[scope]) : scope;
  };

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="h-8 w-8 text-slate-400" />
            {p('title')}
            <button onClick={loadConfigurations} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => { setSelectedConfig(null); setComptabiliteConfigFormTab('generalConfig');
    setShowModal(true); }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_config')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - GPS Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { labelKey: 'stat_total', value: stats.total, icon: Settings, color: 'from-slate-500 to-slate-700' },
          { labelKey: 'stat_active', value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { labelKey: 'stat_fiscal_year', value: stats.exerciceFiscal, icon: Calendar, color: 'from-blue-500 to-blue-600' },
          { labelKey: 'stat_validations', value: stats.validations, icon: Shield, color: 'from-emerald-500 to-emerald-600' },
          { labelKey: 'stat_last_update', value: stats.derniereMAJ, icon: Clock, color: 'from-amber-500 to-amber-600' }
        ].slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            {/* Main View Mode Tabs */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setMainViewMode(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {p(tab.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {p(btn.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Distribution Overview */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p(distributionInfo.titleKey)}</h3>
              <div className="space-y-3">
                {distributionInfo.data.map((item, index) => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="block w-full text-white/70 text-sm font-medium text-right">{p(item.nameKey)}</span>
                        <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
        </div>
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    checked={allRowsSelected}
                    indeterminate={someRowsSelected}
                    onChange={handleSelectAll}
                    thClassName={mergeStickySelectClass('px-3 py-4 text-center align-middle')}
                  />
                  <TableColumnHeaderFilter columnKey="config_ref" label={p('reference')} value={columnFilters.config_ref || ''} onChange={(v) => setColumnFilter('config_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  <TableColumnHeaderFilter columnKey="config_nom" label={p('th_config_name')} value={columnFilters.config_nom || ''} onChange={(v) => setColumnFilter('config_nom', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  <TableColumnHeaderFilter columnKey="config_categorie" label={p('category')} value={columnFilters.config_categorie || ''} onChange={(v) => setColumnFilter('config_categorie', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('th_config_key')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('status')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredConfigurations.length > 0 ? (
                  filteredConfigurations.map((config, index) => (
                    <tr 
                      key={config.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(config.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(config.id)}
                        onChange={() => handleSelectRecord(config.id)}
                        tdClassName={mergeStickySelectClass('px-3 py-4 text-center align-middle')}
                      />
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{config.config_ref || p('not_available')}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{config.config_nom || p('not_available')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{config.config_description || ''}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                          config.config_categorie === 'Général' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' :
                          config.config_categorie === 'Exercice Fiscal' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                          config.config_categorie === 'Plan Comptable' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                          'bg-violet-500/20 text-violet-400 border-violet-500/30'
                        }`}>
                          {categoryLabel(config.config_categorie) || p('not_available')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm font-mono">{config.config_cle || p('not_available')}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={getConfigActiveStatus(config)}
                            recordId={config.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(config)}
                          onEdit={() => handleEdit(config)}
                          onDelete={() => handleDelete(config.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-white/60">
                      <Settings size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredConfigurations.length > 0 ? (
              filteredConfigurations.map((config, index) => (
                <motion.div
                  key={config.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Settings size={20} className="text-emerald-400" />
                          {config.config_nom || p('not_available')}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={getConfigActiveStatus(config)}
                          recordId={config.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_main_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_reference')}</span> {config.config_ref || p('not_available')}</div>
                            <div><span className="font-medium">{p('card_category')}</span> {categoryLabel(config.config_categorie) || p('not_available')}</div>
                            <div><span className="font-medium">{p('card_key')}</span> {config.config_cle || p('not_available')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_config')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_type')}</span> {config.config_type || p('not_available')}</div>
                            <div><span className="font-medium">{p('card_scope')}</span> {scopeLabel(config.scope_config) || p('not_available')}</div>
                            {config.exercice_fiscal_courant && (
                              <div><span className="font-medium">{p('card_exercise')}</span> {config.exercice_fiscal_courant}</div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {config.config_description && (
                              <div className="text-white/60 italic">{config.config_description}</div>
                            )}
                            {config.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('card_notes')}</span>
                                <p className="text-white/60 text-xs mt-1">{config.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(config)}
                      onEdit={() => handleEdit(config)}
                      onDelete={() => handleDelete(config.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Settings size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - GPS ReactDOM Portal with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <Settings className="h-6 w-6 text-blue-400" />
                {selectedConfig ? p('modal_edit_title') : p('modal_new_title')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="config-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedConfig ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {comptabiliteConfigFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = comptabiliteConfigFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setComptabiliteConfigFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="config-form" onSubmit={handleSubmit}>
                {comptabiliteConfigFormTab === 'generalConfig' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_config_name')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.config_nom}
                              onChange={(e) => setFormData({ ...formData, config_nom: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_config_name')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_category')}</label>
                            <CustomSelect
                              value={formData.config_categorie}
                              onChange={(val) => setFormData({ ...formData, config_categorie: val })}
                              options={[
                                { value: 'Général', label: p('category_general'), isDefault: true },
                                { value: 'Exercice Fiscal', label: p('category_fiscal_year'), isDefault: true },
                                { value: 'Plan Comptable', label: p('category_chart'), isDefault: true },
                                { value: 'Clôture', label: p('category_closing'), isDefault: true },
                                { value: 'Rapports', label: p('category_reports'), isDefault: true },
                                { value: 'Validation', label: p('category_validation'), isDefault: true }
                              ]}
                              placeholder={p('placeholder_category')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_config_key')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.config_cle}
                              onChange={(e) => setFormData({ ...formData, config_cle: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_config_key')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_value')}</label>
                            <input
                              type="text"
                              value={formData.config_valeur}
                              onChange={(e) => setFormData({ ...formData, config_valeur: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_value')}
                            />
                          </div>
                          <div className="">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('description')}</label>
                            <textarea
                              value={formData.config_description}
                              onChange={(e) => setFormData({ ...formData, config_description: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_description')}
                            />
                          </div>
</div>
                )}
                {comptabiliteConfigFormTab === 'exerciceCompta' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<ComptabiliteFiscalYearField
                            label={p('label_current_fiscal_year')}
                            value={formData.exercice_fiscal_courant}
                            onChange={(val) => setFormData({ ...formData, exercice_fiscal_courant: val })}
                            placeholder={p('placeholder_fiscal_year')}
                            required
                          />
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_chart_plan')}</label>
                            <CustomSelect
                              value={formData.plan_comptable_reference}
                              onChange={(val) => setFormData({ ...formData, plan_comptable_reference: val })}
                              options={[
                                { value: 'PCN', label: p('opt_pcn'), isDefault: true },
                                { value: 'PCG', label: p('opt_pcg'), isDefault: true },
                                { value: 'IFRS', label: p('opt_ifrs'), isDefault: true },
                                { value: 'US GAAP', label: p('opt_us_gaap'), isDefault: true },
                                { value: 'Custom', label: p('opt_custom_plan'), isDefault: true }
                              ]}
                              placeholder={p('select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_main_currency')}</label>
                            {isBrandCurrencyLocked() ? (
                              <BrandCurrencySelect
                                value={formData.devise_principale || SAUDI_CURRENCY_CODE}
                                onChange={(e) => setFormData({ ...formData, devise_principale: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-default opacity-90"
                              />
                            ) : (
                              <input
                                type="text"
                                value={formData.devise_principale}
                                onChange={(e) => setFormData({ ...formData, devise_principale: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_currency')}
                              />
                            )}
                          </div>
</div>
                )}
                {comptabiliteConfigFormTab === 'validationArchive' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_auto_matching')}</label>
                        <CustomSelect
                          value={formData.lettrage_automatique_actif}
                          onChange={(val) => setFormData({ ...formData, lettrage_automatique_actif: val })}
                          options={[
                            { value: '1', label: p('option_enabled'), isDefault: true },
                            { value: '0', label: p('option_disabled'), isDefault: true },
                          ]}
                          placeholder={p('select')}
                          searchable={false}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_active_controls')}</label>
                        <CustomSelect
                          value={formData.controles_actives}
                          onChange={(val) => setFormData({ ...formData, controles_actives: val })}
                          options={[
                            { value: '1', label: p('option_enabled_plural'), isDefault: true },
                            { value: '0', label: p('option_disabled_plural'), isDefault: true },
                          ]}
                          placeholder={p('select')}
                          searchable={false}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-white/10">
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_auto_backup')}</label>
                        <CustomSelect
                          value={formData.backup_automatique}
                          onChange={(val) => setFormData({ ...formData, backup_automatique: val })}
                          options={[
                            { value: '1', label: p('option_enabled'), isDefault: true },
                            { value: '0', label: p('option_disabled'), isDefault: true },
                          ]}
                          placeholder={p('select')}
                          searchable={false}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_active_config')}</label>
                        <CustomSelect
                          value={formData.actif_config}
                          onChange={(val) => setFormData({ ...formData, actif_config: val })}
                          options={[
                            { value: '1', label: p('state_active'), isDefault: true },
                            { value: '0', label: p('state_inactive'), isDefault: true },
                          ]}
                          placeholder={p('select')}
                          searchable={false}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('notes')}</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('placeholder_notes')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && selectedConfig ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Settings className="h-6 w-6 text-blue-400" />
                {p('view_details_title')}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">{p('view_main_info')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('reference')}</p>
                    <p className="text-white font-semibold">{selectedConfig.config_ref || p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_name')}</p>
                    <p className="text-white font-semibold">{selectedConfig.config_nom || p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('category')}</p>
                    <p className="text-white font-semibold">{categoryLabel(selectedConfig.config_categorie) || p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('key')}</p>
                    <p className="text-white font-semibold font-mono">{selectedConfig.config_cle || p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('status')}</p>
                    <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium ${
                      selectedConfig.actif_config === 1 ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'
                    }`}>
                      {selectedConfig.actif_config === 1 ? p('state_active') : p('state_inactive')}
                    </span>
                  </div>
                </div>
              </div>

              {selectedConfig.config_description && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-2">{p('view_description')}</h3>
                  <p className="text-white/70">{selectedConfig.config_description}</p>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowViewModal(false)}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {p('close')}
            </button>
          </motion.div>
        </div>,
        document.body
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default ComptabiliteConfigPage;
export { ConfigurationSituationPage };
